// Поле с подсказками в стиле панели фильтров (замечание владельца 2026-09-08:
// браузерный datalist выглядел как автозаполнение браузера и выбивался из вида).
// Ввод свободный — как в datalist, но список рисуем сами: .wbox + .wsug.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const PUSTO = '\u0000';   // строка «не задано» в списке — не пересекается с настоящими значениями

/** Выбор из списка с поиском — вместо браузерного выпадающего списка, по образцу
 *  поиска работы в панели фильтров (владелец 2026-09-15). Ввод только отбирает
 *  строки; значение меняется лишь выбором (клик, Enter), Esc и уход — без изменений.
 *  Список рисуется position:fixed: таблицы прокручиваются внутри себя (`.tbl`)
 *  и обрезали бы обычный выпадающий блок; при прокрутке страницы список закрывается.
 *  `pusto` — подпись пустого значения; не задана — очистить нельзя. */
export function VyborSpiska({ value, opts, pusto, podpisi, placeholder, title, disabled, className, style, onPick }: {
  value: string;
  opts: string[];
  pusto?: string;
  /** показ вместо значения (раскатка: ключ «этажи» → «по типовым этажам») */
  podpisi?: Record<string, string>;
  placeholder?: string;
  title?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onPick: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [aktiv, setAktiv] = useState(0);
  const [mesto, setMesto] = useState<React.CSSProperties>({});
  const inp = useRef<HTMLInputElement>(null);
  const pod = (o: string) => (o === PUSTO ? (pusto || '') : podpisi?.[o] ?? o);

  const spisok = useMemo(() => {
    const s = q.trim().toLowerCase();
    const est = opts.filter(o => !s || pod(o).toLowerCase().includes(s));
    return (pusto != null && !s && value ? [PUSTO] : []).concat(est).slice(0, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts, q, pusto, value, podpisi]);

  const otkryt = () => {
    if (disabled) return;
    const r = inp.current?.closest('.wbox')?.getBoundingClientRect();
    if (r) {
      const vniz = window.innerHeight - r.bottom > 270 || r.top < 270;
      setMesto({ display: 'block', position: 'fixed', left: r.left, right: 'auto', width: Math.max(r.width, 220),
        ...(vniz ? { top: r.bottom + 3 } : { top: 'auto', bottom: window.innerHeight - r.top + 3, marginTop: 0 }) });
    }
    setQ(''); setAktiv(Math.max(0, opts.indexOf(value) + (pusto != null && value ? 1 : 0))); setOpen(true);
  };
  const zakryt = () => { setOpen(false); setQ(''); };
  const vybrat = (o: string) => {
    const v = o === PUSTO ? '' : o;
    zakryt(); inp.current?.blur();
    if (v !== value) onPick(v);
  };

  useEffect(() => {
    if (!open) return;
    const pri = (e: Event) => {
      if (e.target instanceof Element && e.target.closest('.vybor-sug')) return;   // прокрутка самого списка
      zakryt(); inp.current?.blur();
    };
    window.addEventListener('scroll', pri, true);
    window.addEventListener('resize', pri);
    return () => { window.removeEventListener('scroll', pri, true); window.removeEventListener('resize', pri); };
  }, [open]);

  // активная строка — в видимой части списка
  const sug = useRef<HTMLDivElement>(null);
  useEffect(() => {
    sug.current?.querySelector('a.hot')?.scrollIntoView({ block: 'nearest' });
  }, [aktiv, open]);

  return (
    <div className={'wbox pw-pole vybor' + (disabled ? ' off' : '') + (value ? '' : ' pusto') + (className ? ' ' + className : '')}
      style={style} title={title} onMouseDown={e => {
        if (disabled || e.target === inp.current) return;
        e.preventDefault(); if (open) { zakryt(); inp.current?.blur(); } else inp.current?.focus();
      }}>
      <input ref={inp} disabled={disabled} autoComplete="off"
        value={open ? q : (value ? pod(value) : '')}
        placeholder={open ? (value ? pod(value) : 'поиск…') : (placeholder ?? '')}
        onFocus={otkryt} onBlur={zakryt}
        onChange={e => { if (!open) otkryt(); setQ(e.target.value); setAktiv(0); }}
        onKeyDown={e => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { e.preventDefault(); otkryt(); return; }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setAktiv(i => (e.key === 'ArrowDown' ? Math.min(i + 1, spisok.length - 1) : Math.max(i - 1, 0)));
          } else if (e.key === 'Enter') {
            e.preventDefault(); if (spisok[aktiv] != null) vybrat(spisok[aktiv]);
          } else if (e.key === 'Escape') {
            e.preventDefault(); e.stopPropagation(); zakryt(); inp.current?.blur();
          }
        }} />
      {!disabled && <span className="vybor-v">▾</span>}
      {/* список — порталом в body (2026-10-07): внутри закреплённой ячейки таблицы его
          перекрывали соседние закреплённые ячейки, свой z-index там не помогает */}
      {open && createPortal(
        <div ref={sug} className="wsug vybor-sug" style={mesto} onMouseDown={e => e.preventDefault()}>
          {spisok.map((o, i) => (
            <a key={o} className={(i === aktiv ? 'hot' : '') + (o === value ? ' tek' : '') + (o === PUSTO ? ' vybor-pusto' : '')}
              onMouseEnter={() => setAktiv(i)}
              onMouseDown={e => { e.preventDefault(); vybrat(o); }}>{pod(o)}</a>
          ))}
          {!spisok.length && <div className="vybor-net">ничего не найдено</div>}
        </div>, document.body)}
    </div>
  );
}

export function PoleSpiska({ value, opts, placeholder, title, disabled, style, dataR, dataF,
  onChange, onEnter, onKeyDown, onBlur, onPaste }: {
  value: string;
  opts: string[];
  placeholder?: string;
  title?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  dataR?: number;                 // адрес ячейки в «Ручном планировании»
  dataF?: string;
  onChange: (v: string) => void;
  onEnter?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}) {
  const [sug, setSug] = useState<string[]>([]);
  const [aktiv, setAktiv] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  const podobrat = (v: string) => {
    const q = v.trim().toLowerCase();
    const est = opts.filter(n => !q || n.toLowerCase().includes(q)).slice(0, 50);
    setSug(est);
    setAktiv(0);
  };
  const vybrat = (n: string) => { onChange(n); setSug([]); };

  return (
    <div className="wbox pw-pole" ref={box} style={style}>
      <input value={value} placeholder={placeholder} title={title} disabled={disabled}
        autoComplete="off" data-r={dataR} data-f={dataF}
        onChange={e => { onChange(e.target.value); podobrat(e.target.value); }}
        onFocus={() => podobrat(value)}
        onPaste={onPaste}
        onBlur={() => { window.setTimeout(() => setSug([]), 150); onBlur?.(); }}
        onKeyDown={e => {
          if (sug.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setAktiv(i => (e.key === 'ArrowDown' ? Math.min(i + 1, sug.length - 1) : Math.max(i - 1, 0)));
            return;
          }
          if (e.key === 'Enter' && sug.length) {
            e.preventDefault();
            vybrat(sug[aktiv] || sug[0]);
            onEnter?.();
            return;
          }
          if (e.key === 'Escape' && sug.length) { setSug([]); return; }
          if (e.key === 'Enter') onEnter?.();
          onKeyDown?.(e);
        }} />
      {sug.length > 0 && (
        <div className="wsug" style={{ display: 'block' }}>
          {sug.map((n, i) => (
            <a key={n} className={i === aktiv ? 'hot' : undefined}
              onMouseDown={e => { e.preventDefault(); vybrat(n); }}>{n}</a>
          ))}
        </div>
      )}
    </div>
  );
}
